import { useLocation, Link } from "wouter";
import { useEffect } from "react";
import { Store, CheckCircle, Image, Clock, Globe, TrendingUp, Crown, Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Footer } from "@/components/Footer";
import { OWNER_PRICING } from "@shared/schema";
import { useI18n } from "@/lib/i18n";
import { useStripePrices, useShopOwnerFeatures } from "@/lib/featureFlags";
import { detectUserCountry } from "@/lib/countryDetection";

const premiumFeatures = [
  {
    icon: CheckCircle,
    titleKey: "ownersKringloop.premium.badge.title",
    descriptionKey: "ownersKringloop.premium.badge.desc"
  },
  {
    icon: Image,
    titleKey: "ownersKringloop.premium.photos.title",
    descriptionKey: "ownersKringloop.premium.photos.desc"
  },
  {
    icon: Clock,
    titleKey: "ownersKringloop.premium.hours.title",
    descriptionKey: "ownersKringloop.premium.hours.desc"
  },
  {
    icon: TrendingUp,
    titleKey: "ownersKringloop.premium.promotions.title",
    descriptionKey: "ownersKringloop.premium.promotions.desc"
  },
  {
    icon: Globe,
    titleKey: "ownersKringloop.premium.website.title",
    descriptionKey: "ownersKringloop.premium.website.desc"
  },
  {
    icon: Crown,
    titleKey: "ownersKringloop.premium.posting.title",
    descriptionKey: "ownersKringloop.premium.posting.desc"
  }
];

const freeFeatures = [
  "ownersKringloop.free.feature1",
  "ownersKringloop.free.feature2",
  "ownersKringloop.free.feature3",
  "ownersKringloop.free.feature4"
];

export default function OwnersKringloopPage() {
  const [, setLocation] = useLocation();
  const { t } = useI18n();
  const { getPrice } = useStripePrices();
  const { canSeeShopOwner } = useShopOwnerFeatures();
  const countryCode = detectUserCountry();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("claim") === "success") {
      setLocation("/account?tab=shop-admin");
    }
  }, [setLocation]);

  // Redirect if shop owner features are disabled
  if (!canSeeShopOwner) {
    setLocation("/");
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">

      <main className="flex-1">
        {/* Help/Instruction Section at the top as requested */}
        <section className="py-16 bg-white border-b">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-12 tracking-tight">{t("ownersKringloop.howTitle")}</h2>
              <div className="grid md:grid-cols-3 gap-12 mt-8">
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 rounded-full bg-primary text-white flex items-center justify-center font-bold text-2xl mb-6 shadow-lg shadow-primary/20">
                    1
                  </div>
                  <h3 className="font-bold text-xl mb-3">{t("ownersKringloop.how.step1.title")}</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("ownersKringloop.how.step1.desc")}
                  </p>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 rounded-full bg-primary text-white flex items-center justify-center font-bold text-2xl mb-6 shadow-lg shadow-primary/20">
                    2
                  </div>
                  <h3 className="font-bold text-xl mb-3">{t("ownersKringloop.how.step2.title")}</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("ownersKringloop.how.step2.desc")}
                  </p>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 rounded-full bg-primary text-white flex items-center justify-center font-bold text-2xl mb-6 shadow-lg shadow-primary/20">
                    3
                  </div>
                  <h3 className="font-bold text-xl mb-3">{t("ownersKringloop.how.step3.title")}</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("ownersKringloop.how.step3.desc")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-gradient-to-b from-primary/5 to-white py-16">
          <div className="container mx-auto px-4 text-center">
            <Badge variant="secondary" className="mb-4 bg-primary/10 text-primary hover:bg-primary/20">{t("ownersKringloop.badge")}</Badge>
            <h1 className="text-3xl md:text-5xl font-bold mb-6 tracking-tight">
              {t("ownersKringloop.title")}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
              {t("ownersKringloop.subtitle")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/kringloopwinkels">
                <Button size="lg" className="h-12 px-8 rounded-full shadow-md hover:shadow-lg transition-all" data-testid="button-find-shop">
                  <Store className="h-5 w-5 mr-2" />
                  {t("ownersKringloop.findShop")}
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-16 items-start">
              <div className="space-y-12">
                <div>
                  <h2 className="text-3xl font-bold mb-8">{t("ownersKringloop.everythingTitle")}</h2>
                  <div className="space-y-6">
                    <Card className="border-none shadow-sm bg-muted/30">
                      <CardContent className="p-6">
                        <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                          <CheckCircle className="h-5 w-5 text-primary" />
                          {t("ownersKringloop.free.title")}
                        </h3>
                        <p className="text-muted-foreground mb-6">
                          {t("ownersKringloop.free.desc")}
                        </p>
                        <ul className="grid sm:grid-cols-2 gap-3">
                          {freeFeatures.map((featureKey, i) => (
                            <li key={i} className="flex items-center gap-3">
                              <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Check className="h-3 w-3 text-primary" />
                              </div>
                              <span className="text-sm font-medium">{t(featureKey)}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-bold mb-8">{t("ownersKringloop.premium.title")}</h3>
                  <div className="grid sm:grid-cols-2 gap-8">
                    {premiumFeatures.map(({ icon: Icon, titleKey, descriptionKey }) => (
                      <div key={titleKey} className="flex gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-primary/5 flex items-center justify-center flex-shrink-0 border border-primary/10">
                          <Icon className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <h4 className="font-bold text-base mb-1">{t(titleKey)}</h4>
                          <p className="text-sm text-muted-foreground leading-relaxed">{t(descriptionKey)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="lg:sticky lg:top-24">
                <Card className="overflow-hidden border-2 border-primary/20 shadow-xl rounded-3xl">
                  <CardHeader className="bg-primary/5 p-8 text-center border-b border-primary/10">
                    <CardTitle className="text-2xl font-bold">{t("ownersKringloop.premiumAccessTitle")}</CardTitle>
                    <p className="text-muted-foreground mt-2">{t("ownersKringloop.oneTimeInvestment")}</p>
                  </CardHeader>
                  <CardContent className="p-8 space-y-8">
                    <div className="text-center">
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-5xl font-extrabold text-primary">
                          €{(getPrice("shop_verification", countryCode)?.amount ?? OWNER_PRICING.firstShop).toFixed(2).replace(".", ",")}
                        </span>
                        <span className="text-muted-foreground font-medium">{t("ownersKringloop.oneTime")}</span>
                      </div>
                      <p className="text-sm font-medium text-muted-foreground mt-4">
                        {t("ownersKringloop.includesSticker")}
                      </p>
                    </div>

                    <div className="space-y-4 pt-4">
                      <h4 className="font-bold text-center text-sm uppercase tracking-wider text-muted-foreground">{t("ownersKringloop.whatYouGet")}</h4>
                      <ul className="grid gap-3">
                        {premiumFeatures.slice(0, 5).map(({ titleKey }) => (
                          <li key={titleKey} className="flex items-center gap-3 text-sm font-medium">
                            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              <Check className="h-3.5 w-3.5 text-primary" />
                            </div>
                            <span>{t(titleKey)}</span>
                          </li>
                        ))}
                        <li className="flex items-center gap-3 text-sm font-medium mb-4">
                          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <Check className="h-3.5 w-3.5 text-primary" />
                          </div>
                          <span>{t("ownersKringloop.andMore")}</span>
                        </li>
                      </ul>
                    </div>

                    <Link href="/kringloopwinkels">
                      <Button className="w-full h-14 rounded-2xl text-lg font-bold bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20" size="lg" data-testid="button-claim-shop">
                        {t("ownersKringloop.claimShop")}
                        <ArrowRight className="h-5 w-5 ml-2" />
                      </Button>
                    </Link>

                    <p className="text-xs text-center text-muted-foreground italic">
                      {t("ownersKringloop.noMonthlyCosts")}
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
